import { useNavigate } from 'react-router-dom'
import { ArrowLeft, FileText } from 'lucide-react'
import { Header } from '../components/landing/Header'
import { Footer } from '../components/landing/Footer'
import { useAuth } from '../contexts/AuthContext'

export function TermsPage() {
  const navigate = useNavigate()
  const { user } = useAuth()

  return (
    <div className="min-h-screen bg-bg flex flex-col overflow-x-hidden">
      <Header
        isAuthenticated={!!user}
        onDashboardClick={() => navigate(user ? '/dashboard' : '/login')}
        onSignUpClick={() => navigate('/signup')}
      />
      <main className="flex-1 pt-24 pb-16 px-4 sm:px-6">
      <div className="container-custom max-w-4xl">
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-text-muted hover:text-accent transition-colors text-sm mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-xl bg-gradient-to-br from-accent to-accent-2">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-text">
                Terms of <span className="text-gradient">Service</span>
              </h1>
              <p className="text-text-muted text-sm mt-0.5">Last updated: March 11, 2026</p>
            </div>
          </div>
        </div>

        <div className="space-y-4">

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">1. Acceptance of Terms</h2>
            <p className="text-text-muted text-sm leading-relaxed">
              By registering for or using the Softitect AI platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not access or use the Service. These Terms constitute a legally binding agreement between you ("User") and Softitect ("Company", "we", "us", or "our").
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">2. Description of Service</h2>
            <p className="text-text-muted text-sm leading-relaxed mb-3">
              Softitect AI provides a scalable API platform that enables developers and businesses to access large language models (LLMs) and AI inference endpoints via a unified REST API. The Service includes:
            </p>
            <ul className="space-y-2 text-sm text-text-muted">
              <li className="flex items-start gap-2">
                <span className="text-accent mt-0.5">•</span>
                Access to multiple AI models via a unified REST API
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent mt-0.5">•</span>
                API key management for authenticating client requests
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent mt-0.5">•</span>
                Usage tracking, credit balance monitoring, and subscription management
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent mt-0.5">•</span>
                A web dashboard for managing your account, API keys, and subscription
              </li>
            </ul>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">3. Account Registration</h2>
            <p className="text-text-muted text-sm leading-relaxed mb-3">
              To use the Service, you must create an account. You agree to:
            </p>
            <ul className="space-y-2 text-sm text-text-muted">
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Provide accurate, current, and complete information during registration</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Maintain and promptly update your account information</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Keep your password and account credentials confidential</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Accept responsibility for all activity that occurs under your account</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Notify us immediately at <a href="mailto:support@softitect.com" className="text-accent hover:text-accent-2 transition-colors">support@softitect.com</a> of any unauthorized use of your account</li>
            </ul>
            <p className="text-text-muted text-sm leading-relaxed mt-3">
              Accounts are registered and authenticated through our identity provider. You are responsible for maintaining the security of your credentials and any API keys generated through the platform.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">4. API Keys and Authentication</h2>
            <p className="text-text-muted text-sm leading-relaxed mb-3">
              Access to the client API endpoints requires a valid API key issued through your account dashboard. You acknowledge and agree that:
            </p>
            <ul className="space-y-2 text-sm text-text-muted">
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>API keys are personal and must not be shared, published, or exposed in client-side code</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>You are solely responsible for all API requests made using your keys</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Compromised keys must be revoked immediately via the dashboard</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>We reserve the right to revoke API keys that violate these Terms</li>
            </ul>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">5. Subscriptions and Billing</h2>
            <p className="text-text-muted text-sm leading-relaxed mb-3">
              The Service is offered on a subscription basis. By subscribing, you agree to the following:
            </p>
            <ul className="space-y-2 text-sm text-text-muted">
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Subscription fees are billed on a recurring basis (monthly or annually, depending on your plan)</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Billing is processed through Paddle, our authorized payment processor</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Your subscription will automatically renew at the end of each billing period unless cancelled</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>You may cancel your subscription at any time through your account dashboard; cancellation takes effect at the end of the current billing period</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>We reserve the right to change pricing with at least 30 days' notice</li>
            </ul>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">6. Refund Policy</h2>
            <p className="text-text-muted text-sm leading-relaxed mb-3">
              Payments for Softitect AI are processed by Paddle, our authorized reseller and Merchant of Record. Refund requests are handled in accordance with Paddle's refund policy.
            </p>
            <p className="text-text-muted text-sm leading-relaxed mb-3">
              You may request a refund within <span className="text-text font-medium">14 days</span> of your initial purchase or, for subscriptions, within <span className="text-text font-medium">14 days from the date your subscription was last renewed</span>. Refund requests outside of this window will not be eligible.
            </p>
            <p className="text-text-muted text-sm leading-relaxed mb-3">
              To request a refund, please contact us at <a href="mailto:support@softitect.com" className="text-accent hover:text-accent-2 transition-colors">support@softitect.com</a> within the applicable 14-day window. Paddle will process approved refunds on our behalf.
            </p>
            <p className="text-text-muted text-sm leading-relaxed">
              If you no longer wish to use the Service, you may cancel your subscription at any time through your account dashboard. Cancellation prevents future charges and your access will continue until the end of the current paid period.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">7. Acceptable Use</h2>
            <p className="text-text-muted text-sm leading-relaxed mb-3">
              You agree not to use the Service to:
            </p>
            <ul className="space-y-2 text-sm text-text-muted">
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Generate content that is illegal, harmful, abusive, defamatory, or violates any third-party rights</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Attempt to circumvent rate limits, usage quotas, or authentication mechanisms</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Reverse engineer, decompile, or attempt to extract the source code of the Service</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Resell or sublicense API access without prior written consent from Softitect</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Use the Service in any way that could damage, disable, or impair our infrastructure</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Violate any applicable local, national, or international law or regulation</li>
            </ul>
            <p className="text-text-muted text-sm leading-relaxed mt-3">
              API requests are subject to rate limits based on your subscription tier. Sustained abuse of the platform may result in temporary throttling or permanent account suspension without refund.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">8. Service Availability</h2>
            <p className="text-text-muted text-sm leading-relaxed mb-3">
              We strive to maintain high availability of the Service but do not guarantee uninterrupted access. We reserve the right to:
            </p>
            <ul className="space-y-2 text-sm text-text-muted">
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Perform scheduled maintenance with advance notice where possible</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Apply emergency updates or patches without prior notice</li>
              <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Modify, suspend, or discontinue any feature of the Service at any time</li>
            </ul>
            <p className="text-text-muted text-sm leading-relaxed mt-3">
              Softitect shall not be liable for any losses or damages arising from service interruptions, downtime, or changes to the Service.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">9. Intellectual Property</h2>
            <p className="text-text-muted text-sm leading-relaxed">
              The Service, including all software, APIs, documentation, and branding, is the exclusive property of Softitect and is protected by applicable intellectual property laws. You are granted a limited, non-exclusive, non-transferable license to use the Service solely as permitted by these Terms. You retain ownership of any content or data you submit through the API, but grant Softitect a limited license to process such data solely for the purpose of providing the Service.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">10. Disclaimer of Warranties</h2>
            <p className="text-text-muted text-sm leading-relaxed">
              The Service is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement. We do not warrant that the Service will be error-free, uninterrupted, or that AI-generated outputs will be accurate, complete, or suitable for any particular purpose.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">11. Limitation of Liability</h2>
            <p className="text-text-muted text-sm leading-relaxed">
              To the maximum extent permitted by applicable law, Softitect shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, business, or goodwill, arising out of or in connection with your use of the Service, even if advised of the possibility of such damages. Our total aggregate liability to you for any claims arising under these Terms shall not exceed the amount you paid to us in the three (3) months preceding the claim.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">12. Termination</h2>
            <p className="text-text-muted text-sm leading-relaxed">
              We may suspend or terminate your account and access to the Service at our sole discretion, with or without notice, if you breach these Terms or engage in conduct we deem harmful to the Service or other users. Upon termination, your right to use the Service ceases immediately. Provisions of these Terms that by their nature should survive termination shall survive, including but not limited to the refund policy, limitation of liability, and intellectual property sections.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">13. Changes to These Terms</h2>
            <p className="text-text-muted text-sm leading-relaxed">
              We reserve the right to update these Terms at any time. Material changes will be communicated via email or a prominent notice on the platform at least 30 days before taking effect. Your continued use of the Service after the effective date of any changes constitutes your acceptance of the revised Terms.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">14. Governing Law</h2>
            <p className="text-text-muted text-sm leading-relaxed">
              These Terms shall be governed by and construed in accordance with applicable law. Any disputes arising under these Terms shall be resolved through binding arbitration or in the courts of competent jurisdiction, as applicable.
            </p>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text mb-3">15. Contact</h2>
            <p className="text-text-muted text-sm leading-relaxed mb-3">
              For questions about these Terms, please reach out to us:
            </p>
            <div className="p-4 rounded-lg bg-bg border border-border">
              <p className="text-sm text-text-muted">
                Email: <a href="mailto:support@softitect.com" className="text-accent hover:text-accent-2 transition-colors">support@softitect.com</a>
              </p>
            </div>
          </div>

        </div>
      </div>
      </main>
      <Footer />
    </div>
  )
}
